// Pages/inventario/components/DeleteProductModal.tsx
// Doble confirmación EXPLÍCITA para eliminar productos (round 2026-07-26).
//
// Decisión del owner: eliminar un producto es muy peligroso (borra el
// producto Y todo su registro financiero — el egreso desaparece de
// finanzas-empresa, los cobros del panel del cliente, los reembolsos del
// ledger del tercero, y el historial). Por eso:
//   Paso 1 — lista explícita de TODO lo que se borrará, a quiénes afecta y
//            en qué fechas (endpoint /impacto-eliminacion), con advertencia
//            especial si hay MESES YA CERRADOS para el cliente.
//   Paso 2 — segunda confirmación: escribir el nombre del producto (o
//            "ELIMINAR" en el borrado masivo) para habilitar el botón rojo.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Modal, Spin, Tag, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { inventarioApi } from "../../../services/inventario.api";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import type { Producto } from "../useInventarioLogic";

const { Text } = Typography;

const CLP = (n: number) => `$${Number(n || 0).toLocaleString("es-CL")}`;

interface ImpactoData {
  producto: {
    id: number;
    nombre: string;
    categoria: string | null;
    cantidadUnidades: number;
    cantidadPacks: number;
  };
  transacciones: {
    total: number;
    compras: { count: number; monto: number };
    movimientos: { count: number; monto: number };
    primeraFecha: string | null;
    ultimaFecha: string | null;
  };
  recuperaciones: Array<{
    id: number;
    monto: number;
    estadoPago: string | null;
    fecha: string;
    propiedadNombre: string | null;
    clienteNombre: string | null;
  }>;
  reembolsos: Array<{
    id: number;
    monto: number;
    estadoPago: string | null;
    fecha: string;
    terceroNombre: string | null;
  }>;
  historialCount: number;
  mesesCerradosAfectados: Array<{
    periodo: string;
    propiedadNombre: string | null;
  }>;
}

interface DeleteProductModalProps {
  open: boolean;
  /** 1 producto = borrado individual; N = borrado masivo. */
  productos: Producto[];
  deleting: boolean;
  onCancel: () => void;
  /** El padre ejecuta el borrado real (onDelete / onBulkDelete). */
  onConfirm: () => Promise<void>;
}

const DeleteProductModal: React.FC<DeleteProductModalProps> = ({
  open,
  productos,
  deleting,
  onCancel,
  onConfirm,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [impactos, setImpactos] = useState<ImpactoData[]>([]);
  const [confirmText, setConfirmText] = useState("");
  // Nº de impactos que NO se pudieron calcular. Sin esto, un 500 del endpoint
  // se veía como "0 compras, 0 cobros, $0" — la doble confirmación reportaba
  // "sin impacto" justo cuando NO lo conoce, y dejaba borrar igual.
  const [fallidos, setFallidos] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  const esMasivo = productos.length > 1;
  // Texto exigido en el paso 2: el NOMBRE del producto (individual) o la
  // palabra ELIMINAR (masivo). Comparación case-insensitive y sin espacios
  // sobrantes — la fricción es intencional, no un test de tipeo perfecto.
  const textoRequerido = esMasivo
    ? "ELIMINAR"
    : (productos[0]?.nombre ?? "").trim();
  const confirmado =
    confirmText.trim().toLowerCase() === textoRequerido.toLowerCase() &&
    textoRequerido !== "";

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setConfirmText("");
    setImpactos([]);
    setFallidos(0);
    if (productos.length === 0) return;
    setLoading(true);
    Promise.all(
      productos.map((p) => inventarioApi.getImpactoEliminacion(p.id)),
    )
      .then((resps) => {
        const ok = resps
          .map((r) => r?.data as ImpactoData | undefined)
          .filter(Boolean) as ImpactoData[];
        setImpactos(ok);
        setFallidos(resps.length - ok.length);
      })
      .catch(() => setFallidos(productos.length))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reloadToken]);

  // Agregados sobre todos los productos (para el masivo; con 1 producto son
  // sus propios números).
  const agg = useMemo(() => {
    const a = {
      compras: 0,
      comprasMonto: 0,
      movimientos: 0,
      recuperaciones: 0,
      recuperacionesMonto: 0,
      reembolsos: 0,
      reembolsosMonto: 0,
      historial: 0,
      mesesCerrados: [] as Array<{ periodo: string; propiedadNombre: string | null }>,
      clientes: new Set<string>(),
      terceros: new Set<string>(),
    };
    for (const i of impactos) {
      a.compras += i.transacciones.compras.count;
      a.comprasMonto += i.transacciones.compras.monto;
      a.movimientos += i.transacciones.movimientos.count;
      a.recuperaciones += i.recuperaciones.length;
      a.recuperacionesMonto += i.recuperaciones.reduce((s, r) => s + r.monto, 0);
      a.reembolsos += i.reembolsos.length;
      a.reembolsosMonto += i.reembolsos.reduce((s, r) => s + r.monto, 0);
      a.historial += i.historialCount;
      for (const m of i.mesesCerradosAfectados) a.mesesCerrados.push(m);
      for (const r of i.recuperaciones) {
        if (r.clienteNombre) a.clientes.add(r.clienteNombre);
      }
      for (const r of i.reembolsos) {
        if (r.terceroNombre) a.terceros.add(r.terceroNombre);
      }
    }
    return a;
  }, [impactos]);

  const tituloProductos = esMasivo
    ? `${productos.length} productos`
    : `"${productos[0]?.nombre ?? ""}"`;

  return (
    <Modal
      open={open}
      onCancel={deleting ? undefined : onCancel}
      title={
        <span>
          <ExclamationCircleOutlined style={{ color: "#cf1322", marginRight: 8 }} />
          {step === 1
            ? `Eliminar ${tituloProductos} — revisa el impacto`
            : `ÚLTIMA CONFIRMACIÓN — eliminar ${tituloProductos}`}
        </span>
      }
      footer={
        step === 1
          ? [
              <Button key="cancel" onClick={onCancel}>
                Cancelar
              </Button>,
              fallidos > 0 ? (
                <Button
                  key="retry"
                  type="primary"
                  loading={loading}
                  onClick={() => setReloadToken((t) => t + 1)}
                >
                  Reintentar
                </Button>
              ) : (
                <Button
                  key="next"
                  danger
                  type="primary"
                  disabled={loading}
                  onClick={() => setStep(2)}
                >
                  Entiendo el impacto, continuar
                </Button>
              ),
            ]
          : [
              <Button key="back" onClick={() => setStep(1)} disabled={deleting}>
                Volver
              </Button>,
              <Button
                key="confirm"
                danger
                type="primary"
                disabled={!confirmado}
                loading={deleting}
                onClick={async () => {
                  await onConfirm();
                }}
              >
                Eliminar definitivamente
              </Button>,
            ]
      }
      width={640}
      maskClosable={false}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
          <div style={{ marginTop: 8 }}>Calculando el impacto…</div>
        </div>
      ) : step === 1 ? (
        <div>
          {fallidos > 0 && (
            <Alert
              type="warning"
              showIcon
              message="No se pudo calcular el impacto"
              description={
                fallidos === productos.length
                  ? "No se pudo consultar qué se eliminaría (falla del servidor o de la conexión). Los números de abajo NO son confiables — reintenta antes de borrar."
                  : `No se pudo consultar el impacto de ${fallidos} de ${productos.length} productos. Los totales de abajo están INCOMPLETOS — reintenta antes de borrar.`
              }
              style={{ marginBottom: 12 }}
            />
          )}
          <Alert
            type="error"
            showIcon
            message="Esta acción es IRREVERSIBLE"
            description="Se elimina el producto y TODO su registro financiero. Si solo quieres sacarlo del inventario sin tocar finanzas (falla, pérdida, robo, venta…), usa OCULTAR en su lugar — la baja no borra nada."
            style={{ marginBottom: 12 }}
          />
          <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
            <li>
              <Text strong>
                {agg.compras + agg.movimientos} transacción
                {agg.compras + agg.movimientos === 1 ? "" : "es"}
              </Text>{" "}
              ({agg.compras} compra{agg.compras === 1 ? "" : "s"} por{" "}
              <Text strong>{CLP(agg.comprasMonto)}</Text>
              {agg.movimientos > 0
                ? ` y ${agg.movimientos} movimiento${agg.movimientos === 1 ? "" : "s"}`
                : ""}
              ) — el egreso desaparece de Finanzas Empresa (totalCompras).
            </li>
            <li>
              <Text strong>
                {agg.recuperaciones} cobro{agg.recuperaciones === 1 ? "" : "s"} al
                cliente
              </Text>{" "}
              por <Text strong>{CLP(agg.recuperacionesMonto)}</Text>
              {agg.clientes.size > 0 && (
                <> — afecta a: {[...agg.clientes].join(", ")}</>
              )}{" "}
              — desaparecen del panel del cliente.
            </li>
            <li>
              <Text strong>
                {agg.reembolsos} reembolso{agg.reembolsos === 1 ? "" : "s"} a
                terceros
              </Text>{" "}
              por <Text strong>{CLP(agg.reembolsosMonto)}</Text>
              {agg.terceros.size > 0 && <> — afecta a: {[...agg.terceros].join(", ")}</>}{" "}
              — desaparecen de su ledger (incluidos los pendientes de pago).
            </li>
            <li>
              <Text strong>{agg.historial} registro{agg.historial === 1 ? "" : "s"} de historial</Text>{" "}
              — se borran (información irrecuperable, sin deshacer).
            </li>
          </ul>
          {!esMasivo && impactos[0] && (
            <div style={{ marginBottom: 12, fontSize: 12, color: "#6b7280" }}>
              {impactos[0].transacciones.primeraFecha && (
                <>
                  Registros financieros desde el{" "}
                  {impactos[0].transacciones.primeraFecha} hasta el{" "}
                  {impactos[0].transacciones.ultimaFecha}.{" "}
                </>
              )}
              {impactos[0].recuperaciones.map((r) => (
                <div key={r.id}>
                  • Cobro de {CLP(r.monto)} ({r.fecha}) en{" "}
                  {r.propiedadNombre ? formatPropertyName(r.propiedadNombre) : "—"}
                  {r.clienteNombre ? ` — cliente ${r.clienteNombre}` : ""}
                </div>
              ))}
              {impactos[0].reembolsos.map((r) => (
                <div key={r.id}>
                  • Reembolso de {CLP(r.monto)} ({r.fecha}) a{" "}
                  {r.terceroNombre ?? "—"}{" "}
                  {r.estadoPago === "pendiente" ? (
                    <Tag color="orange">pendiente</Tag>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          {agg.mesesCerrados.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="⚠️ Afecta meses YA CERRADOS para el cliente"
              description={
                <>
                  Estos períodos ya fueron publicados y el cliente vio sus
                  números; al eliminar, sus totales cambiarán retroactivamente:{" "}
                  {agg.mesesCerrados
                    .map(
                      (m) =>
                        `${m.periodo}${m.propiedadNombre ? ` (${formatPropertyName(m.propiedadNombre)})` : ""}`,
                    )
                    .join(", ")}
                  .
                </>
              }
            />
          )}
        </div>
      ) : (
        <div>
          <Alert
            type="error"
            showIcon
            message={
              esMasivo
                ? `Se eliminarán ${productos.length} productos y toda su información financiera.`
                : `"${productos[0]?.nombre}" será eliminado PERMANENTEMENTE junto a toda su información financiera.`
            }
            style={{ marginBottom: 12 }}
          />
          <p>
            Para confirmar, escribe{" "}
            <Text code strong>
              {textoRequerido}
            </Text>{" "}
            y presiona el botón rojo:
          </p>
          <Input
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={textoRequerido}
            onPressEnter={() => {
              if (confirmado && !deleting) void onConfirm();
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default DeleteProductModal;
